import t from '@rcm/i18n-client';
import { Col, Form, SubmitHandler } from 'forms';
import React, { ReactNode } from 'react';
import '../../../styles/credit-card.scss';
import { MESSAGE_CREDIT_CARD_COULD_NOT_BE_AUTHENTICATED } from '@reliv/cart';

interface Props {
  /**
   * Contents of the form.
   *
   * This should contain one instance of `CreditCardForm.Body` and, optionally,
   * one instance of `CreditCardForm.Foot` as a sibling.
   */
  children?: ReactNode;
  /**
   * Submission handler.
   *
   * This is called in response to things that would normally cause a form
   * to be submitted.
   */
  onSubmit?: SubmitHandler<HTMLFormElement>;

  /**
   * Toggle display of "Credit Card Information" header
   */
  cartV2?: boolean;
}

/**
 * Standard container for credit card forms.
 *
 * This provides the overarching document structure typically required for
 * credit card forms.
 *
 * The standard head area of the form is included with this implementation
 * because it's currently unclear how head code should be factored out due to
 * existing legacy layout.
 *
 * Contents for the form should be provided using `CreditCardForm.Body` and,
 * optionally, `CreditCardForm.Foot`.
 *
 * @example
 * ```jsx
 * <CreditCardForm>
 *    <CreditCardForm.Body>
 *      ...body contents...
 *    </CreditCardForm.Body>
 *    <CreditCardForm.Foot>
 *      ...foot contents...
 *    </CreditCardForm.Foot>
 * </CreditCardForm>
 * ```
 */
const CreditCardForm = ({ children, onSubmit, cartV2 }: Props) => {
  const isNotAuthenticated = window.location.href.includes(
    'showMessageCreditCardCouldNotBeAuthenticated=1'
  );

  return (
    <div className='credit-card-form'>
      {isNotAuthenticated && (
        <div className='alert alert-warning'>
          {t(MESSAGE_CREDIT_CARD_COULD_NOT_BE_AUTHENTICATED)}
        </div>
      )}
      {!cartV2 ? (
        <div className='cartHeader'>
          <Col xs={10}>{t('Credit Card Information')}</Col>
        </div>
      ) : null}
      <Form autoComplete='off' onSubmit={onSubmit}>
        {children}
      </Form>
    </div>
  );
};

interface CreditCardFormBodyProps {
  children?: ReactNode;
  fullwidth?: Boolean;
}

/**
 * Contains the body portion of a credit card form.
 */
CreditCardForm.Body = ({
  fullwidth = false,
  children,
}: CreditCardFormBodyProps) => (
  <Col xs={12} sm={12} className='credit-card-form-body'>
    {children}
  </Col>
);

interface CreditCardFormFootProps {
  children?: ReactNode;
}

/**
 * Contains the foot portion of a credit card form.
 */
CreditCardForm.Foot = ({ children }: CreditCardFormFootProps) => (
  <Col xs={12} className='credit-card-form-foot'>
    {children}
  </Col>
);

export default CreditCardForm;
